import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import nom35AnalyticsApi from '../../api/nom35AnalyticsApi';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList
} from 'recharts';
import './Nom035JobAreasChart.css';
import DownloadChartButton from '../DownloadChartButton/DownloadChartButton';

/**
 * Props:
 * - title           Texto a mostrar como título de la gráfica.
 * - companyId       ID de la empresa. Si no se pasa, no hace llamado.
 * - metric          "global" | categoría clave | dominio clave.
 * - filterConfigs   Array de { name, label, options } para los filtros.
 */
export default function Nom035JobAreasChart({
  title,
  companyId,
  applicationId = null,
  applicationIds = [],
  filters: controlledFilters,
  metric = 'global'
}) {
  const filters = controlledFilters ?? {};
  const [data, setData] = useState({ jobareas: [], ranges: [] });
  const [loading, setLoading] = useState(false);
  const applicationIdsKey = applicationIds.join(',');

  // Definimos el orden de niveles y colores
  const levelOrder = ['Nulo', 'Bajo', 'Medio', 'Alto', 'Muy alto'];
  const colors = [
    '#1069f1',
    '#00be9c',
    '#ffe951',
    '#fd7e14',
    '#dc3545'
  ];

  const getColorForRisk = (risk) => {
    const idx = levelOrder.indexOf(risk);
    return colors[idx] || 'gray';
  };

  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    let active = true;
    const loadData = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const res = await nom35AnalyticsApi.getJobAreasSummaryByCompanyId(
          companyId,
          metric,
          {
            ...JSON.parse(filterKey),
            ...(applicationId ? { applicationId } : {}),
            ...(applicationIdsKey ? { applicationIds: applicationIdsKey } : {}),
          }
        );
        if (!active) return;
        setData({ jobareas: res.jobareas, ranges: res.ranges });
      } catch (err) {
        if (!active) return;
        console.error('Error cargando resumen por áreas de trabajo:', err);
        setData({ jobareas: [], ranges: [] });
      } finally {
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => { active = false; };
  }, [companyId, applicationId, applicationIdsKey, metric, filterKey]);

  // Altura dinámica: crece con el número de áreas para que no se corten
  const chartHeight = Math.max(300, data.jobareas.length * 52);

  return (
    <div className="nom035-jobareas-chart">
      <div className="nom035-chart-title-row">
        <h3 className="chart-title">{title}</h3>
        {!loading && data.jobareas.length > 0 && (
          <DownloadChartButton title={title} targetSelector=".nom035-jobareas-chart" />
        )}
      </div>

      <div className="chart-wrapper">
        {loading ? (
          <p>Cargando datos…</p>
        ) : data.jobareas.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              layout="vertical"
              data={data.jobareas}
              margin={{ top: 10, right: 60, left: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <YAxis
                type="category"
                dataKey="jobArea"
                width={200}
                tick={{ fontSize: 12, fill: 'black' }}
              />
              <XAxis
                type="number"
                tickFormatter={val => val.toFixed(1)}
              />
              <Tooltip formatter={val => val.toFixed(2)} />
              <Bar dataKey="score" barSize={28}>
                {data.jobareas.map((entry) => (
                  <Cell
                    key={`cell-${entry.jobArea}`}
                    fill={getColorForRisk(entry.risk)}
                  />
                ))}
                <LabelList
                  dataKey="score"
                  position="right"
                  formatter={val => val.toFixed(2)}
                  style={{ fill: 'black', fontSize: 12 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p>No hay datos para mostrar.</p>
        )}
      </div>

      {data.ranges.length > 0 && (
        <div className="legend-section">
          <p className="legend-title">Rango de puntajes:</p>
          <ul className="risk-legend">
            {data.ranges.map((rng, i) => {
              const label = rng.max !== undefined
                ? rng.min !== undefined
                  ? `${rng.min} ≤ puntaje < ${rng.max}`
                  : `< ${rng.max}`
                : `≥ ${rng.min}`;
              return (
                <li key={rng.level}>
                  <span
                    className="legend-bullet"
                    style={{ backgroundColor: colors[i] }}
                  />
                  {rng.level} ({label})
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

Nom035JobAreasChart.propTypes = {
  title: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  applicationId: PropTypes.string,
  applicationIds: PropTypes.arrayOf(PropTypes.string),
  metric: PropTypes.string,
  filterConfigs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.string)
    })
  )
};
